import React, { useState, useMemo, useEffect } from 'react';
import _get from 'lodash/get';
import _without from 'lodash/without';
import _keyBy from 'lodash/keyBy';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';
import { Field } from '@sift/types';
import * as Types from 'pages/Features/typings';

import Typography from '@material-ui/core/Typography';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';

interface Props {
  fields: Field[];
  values: Types.FeatureValues;
  view: string;
  onChange: (field: string, value: any) => void;
}

function OrgLevels({ fields, values, view, onChange }: Props) {
  const classes = useStyles();

  const keyedFields = useMemo(() => _keyBy(fields, 'objectKey'), [fields]);

  const layoutValue = _get(values, ['layouts', 'org-levels']);
  const [layout, setLayout] = useState(layoutValue);

  const visibleOrgLevelKeys = _get(
    values,
    ['person', 'directory', 'orgChart', 'cardContent', view, 'orgLevels'],
    [],
  );
  const orgLevelOptions = layout.map((item: Types.LayoutItem) => item.name);
  const validVisibleOrgLevelKeys = visibleOrgLevelKeys.filter((k: string) =>
    orgLevelOptions.includes(k),
  );

  useEffect(() => {
    setLayout(layoutValue);
  }, [layoutValue]);

  const renderHeader = () => {
    return (
      <>
        <Typography variant="h6">Organization Levels</Typography>
        <Typography variant="body2">Select organization levels to be shown on the card.</Typography>
      </>
    );
  };

  if (!layout.length) {
    return (
      <div className={classes.root}>
        {renderHeader()}
        <div className={classes.emptyState}>
          <Typography variant="body2">There are currently no org levels to show</Typography>
        </div>
      </div>
    );
  }

  const handleToggle = (objectKey: string) => {
    let attrKeys = [...validVisibleOrgLevelKeys];

    if (attrKeys.includes(objectKey)) {
      attrKeys = _without(attrKeys, objectKey);
    } else {
      attrKeys.push(objectKey);
    }

    return onChange(`person.directory.orgChart.cardContent.${view}.orgLevels`, attrKeys);
  };

  return (
    <div className={classes.root}>
      {renderHeader()}
      <FormGroup className={classes.formContainer}>
        <FormGroup>
          {orgLevelOptions.map((attrKey: string, index: number) => {
            const field = keyedFields[attrKey];
            const fieldDisplayName = field.displayName;

            return (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={validVisibleOrgLevelKeys.includes(attrKey)}
                    name={fieldDisplayName}
                    onChange={() => handleToggle(attrKey)}
                    aria-label="ios-switch-on"
                    className={classes.switchMargin}
                  />
                }
                label={<span className={classes.label}>{fieldDisplayName}</span>}
                key={`${attrKey}-${index}`}
              />
            );
          })}
        </FormGroup>
      </FormGroup>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3),
    width: '100%',
    maxWidth: 372,
    [theme.breakpoints.down('lg')]: {
      maxWidth: 336,
    },
  },
  formContainer: {
    marginTop: theme.spacing(),
  },
  switchMargin: {
    margin: theme.spacing(1.5),
  },
  emptyState: {
    marginTop: theme.spacing(11),
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(5),
    },
  },
  label: {
    display: 'flex',
    alignItems: 'center',
  },
}));

export default OrgLevels;
